import { useEffect, useMemo, useState, type ReactElement } from 'react'
import {
  Box,
  Sheet,
  Stack,
  Typography,
  Card,
  CardContent,
  Grid,
  Input,
  Select,
  Option,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  Alert
} from '@mui/joy'
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Replay as ReplayIcon,
  Facebook as FacebookIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  RemoveCircle as RemoveCircleIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material'
import api from '../services/api'

// ─── Tipos ────────────────────────────────────────────────────────
type Status = 'pending' | 'sent' | 'success' | 'failed' | 'skipped'

interface LeadEventRow {
  id: number
  companyId: number
  ticketId: number | null
  contactId: number | null
  kanbanTagId: number | null
  kanbanKey: string | null
  kanbanTagName: string | null
  eventName: string
  eventId: string | null
  source: string | null
  destinationId: string | null
  destinationSource: string | null
  responseStatus: Status
  fbtraceId: string | null
  errorMessage: string | null
  userData: Record<string, unknown> | null
  customData: Record<string, unknown> | null
  fbResponse: Record<string, unknown> | null
  sentAt: string | null
  createdAt: string
  updatedAt: string
  contact?: { id: number; name: string; number: string; email?: string }
  kanbanTag?: {
    id: number
    name: string
    key: string
    color?: string
    kanban?: number
    metaConversionName?: string | null
    metaEventName?: string | null
    metaLeadStatus?: string | null
    metaRule?: string | null
    metaCustomEventType?: string | null
    metaCustomConversionId?: string | null
    metaConversionStatus?: string | null
    metaLastSyncAt?: string | null
    metaLastError?: string | null
  }
  ticket?: { id: number; status: string; lastMessage?: string; updatedAt: string }
}

interface StatsResponse {
  total: number
  success: number
  failed: number
  pending: number
  sent: number
  skipped: number
}

const STATUS_LABELS: Record<Status, string> = {
  pending: 'Pendiente',
  sent: 'Enviada sin confirmar',
  success: 'Recibida por Meta',
  failed: 'Fallido',
  skipped: 'Omitido'
}

const STATUS_COLOR: Record<
  Status,
  'success' | 'danger' | 'warning' | 'primary' | 'neutral'
> = {
  pending: 'warning',
  sent: 'primary',
  success: 'success',
  failed: 'danger',
  skipped: 'neutral'
}

const STATUS_ICON: Record<Status, ReactElement> = {
  pending: <PendingIcon fontSize="small" />,
  sent: <TrendingUpIcon fontSize="small" />,
  success: <CheckCircleIcon fontSize="small" />,
  failed: <ErrorIcon fontSize="small" />,
  skipped: <RemoveCircleIcon fontSize="small" />
}

const formatDate = (iso?: string | null) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const maskId = (value?: string | null) => {
  if (!value) return '—'
  if (value.length <= 8) return value
  return `${value.slice(0, 4)}…${value.slice(-4)}`
}

const truncate = (value?: string | null, max = 60) => {
  if (!value) return '—'
  if (value.length <= max) return value
  return `${value.slice(0, max)}…`
}

const getMetaError = (row: LeadEventRow) => {
  const responseError = row.fbResponse?.error as
    | { message?: string; error_user_msg?: string; error_user_title?: string; code?: number; error_subcode?: number }
    | undefined
  return {
    title: responseError?.error_user_title,
    message: row.errorMessage || responseError?.error_user_msg || responseError?.message || '',
    code: responseError?.code,
    subcode: responseError?.error_subcode
  }
}

const getEventsReceived = (row: LeadEventRow) => {
  const value = row.fbResponse?.events_received
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getAttributionInfo = (row: LeadEventRow) => {
  const mode = row.customData?.attribution_mode
  const hasCtwa = Boolean(row.userData?.ctwa_clid)

  if (mode === 'phone_physical_store') {
    return {
      label: 'Por teléfono',
      detail: 'Meta recibió el evento usando el número del contacto. No necesitó ctwa_clid.'
    }
  }

  if (hasCtwa) {
    return {
      label: 'Click de anuncio WhatsApp',
      detail: 'Se envió con ctwa_clid, útil para atribución directa de campañas click-to-WhatsApp.'
    }
  }

  return {
    label: 'Datos del contacto',
    detail: 'Se envió con los datos disponibles del contacto.'
  }
}

const getMetaLocation = (row: LeadEventRow) => {
  const conversionName = row.kanbanTag?.metaConversionName || row.kanbanTagName || 'Conversión Kanban'
  const conversionId = row.kanbanTag?.metaCustomConversionId || '—'
  const pixelId = row.destinationId || '—'

  return {
    conversionName,
    conversionId,
    pixelId,
    path: 'Administrador de eventos > Conjuntos de datos > abre el pixel/dataset indicado > Conversiones personalizadas'
  }
}

const wasSentBeforeCurrentConversion = (row: LeadEventRow) => {
  if (!row.sentAt || !row.kanbanTag?.metaLastSyncAt) return false
  const sent = new Date(row.sentAt).getTime()
  const synced = new Date(row.kanbanTag.metaLastSyncAt).getTime()
  return Number.isFinite(sent) && Number.isFinite(synced) && sent < synced
}

const getFixSuggestion = (message?: string, subcode?: number) => {
  const text = (message || '').toLowerCase()

  if (text.includes('access token') || text.includes('token')) {
    return 'El token de Meta no fue aceptado. Revisa la conexión de Meta y vuelve a guardar/sincronizar credenciales.'
  }
  if (subcode === 2804066 || text.includes('tipo de evento no válido')) {
    return 'El evento elegido no es válido para mensajes de WhatsApp. Usa LeadSubmitted/Lead para etapas de lead.'
  }
  if (subcode === 2804069 || text.includes('page_id')) {
    return 'Meta pidió page_id. Vincula una conexión Facebook Page activa a la empresa.'
  }
  if (subcode === 2804071 || text.includes('ctwa_clid')) {
    return 'Meta pidió ctwa_clid para atribución de WhatsApp. Si el ticket no viene de anuncio, usa el modo por teléfono.'
  }
  if (text.includes('sin destino')) {
    return 'No hay pixel/dataset activo para esta empresa. Sincroniza el conjunto de datos de Meta.'
  }
  if (text.includes('contacto')) {
    return 'El ticket no tenía contacto asociado. Asocia o corrige el contacto antes de reenviar.'
  }

  return message || 'Revisa la configuración de la etiqueta, el pixel/dataset y la conexión de Meta.'
}

const getInitialQueryParam = (name: string) => {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get(name) || ''
}

const getUserSummary = (row: LeadEventRow) => {
  if (row.responseStatus === 'success') {
    const received = getEventsReceived(row)
    return {
      color: 'success' as const,
      title: received > 0 ? 'Meta recibió esta conversión' : 'Meta respondió correctamente',
      detail: received > 0
        ? 'Meta confirmó ' + received + ' evento(s) recibido(s) en el pixel/dataset.'
        : 'La solicitud fue aceptada por Meta.'
    }
  }

  if (row.responseStatus === 'failed') {
    const metaError = getMetaError(row)
    return {
      color: 'danger' as const,
      title: 'No se pudo enviar a Meta',
      detail: getFixSuggestion(metaError.message, metaError.subcode)
    }
  }

  if (row.responseStatus === 'pending') {
    return {
      color: 'warning' as const,
      title: 'Pendiente de envío',
      detail: 'ChatEAM creó el registro, pero todavía no hay confirmación de Meta.'
    }
  }

  if (row.responseStatus === 'skipped') {
    return {
      color: 'neutral' as const,
      title: 'No se envió',
      detail: row.errorMessage || 'El sistema omitió este evento porque faltaba una condición necesaria.'
    }
  }

  return {
    color: 'primary' as const,
    title: 'Enviada a Meta, sin recepción confirmada',
    detail: 'Meta respondió la solicitud, pero no confirmó events_received mayor a cero. Revisa el ID de soporte Meta.'
  }
}

const JsonBlock = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined) {
    return (
      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
        — vacío —
      </Typography>
    )
  }
  let text = ''
  try {
    text =
      typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  } catch {
    text = String(value)
  }
  return (
    <Sheet
      variant="soft"
      sx={{
        p: 1.5,
        borderRadius: 'sm',
        maxHeight: 280,
        overflow: 'auto'
      }}
    >
      <Typography
        level="body-xs"
        component="pre"
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          m: 0
        }}
      >
        {text}
      </Typography>
    </Sheet>
  )
}

export default function KanbanLeadConversions({ embedded = false }: { embedded?: boolean }) {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<LeadEventRow[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)

  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)

  // Filtros
  const [q, setQ] = useState(() => getInitialQueryParam('q'))
  const [qInput, setQInput] = useState(() => getInitialQueryParam('q'))
  const [status, setStatus] = useState<string>(() => getInitialQueryParam('status'))
  const [kanbanKey, setKanbanKey] = useState(() => getInitialQueryParam('kanbanKey'))
  const [ticketId, setTicketId] = useState(() => getInitialQueryParam('ticketId'))
  const [contactId, setContactId] = useState(() => getInitialQueryParam('contactId'))
  const [dateFrom, setDateFrom] = useState(() => getInitialQueryParam('dateFrom'))
  const [dateTo, setDateTo] = useState(() => getInitialQueryParam('dateTo'))

  // Detalle
  const [selected, setSelected] = useState<LeadEventRow | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / limit)),
    [count, limit]
  )

  // ─── Fetchers ──────────────────────────────────────────────────
  const fetchList = async (
    overrides: Partial<{
      page: number
      q: string
      status: string
      kanbanKey: string
      ticketId: string
      contactId: string
      dateFrom: string
      dateTo: string
    }> = {}
  ) => {
    try {
      setLoading(true)
      const effective = {
        page,
        q,
        status,
        kanbanKey,
        ticketId,
        contactId,
        dateFrom,
        dateTo,
        ...overrides
      }
      const params: Record<string, string | number> = { page: effective.page, limit }
      if (effective.q) params.q = effective.q
      if (effective.status) params.status = effective.status
      if (effective.kanbanKey) params.kanbanKey = effective.kanbanKey
      if (effective.ticketId) params.ticketId = effective.ticketId
      if (effective.contactId) params.contactId = effective.contactId
      if (effective.dateFrom) params.dateFrom = effective.dateFrom
      if (effective.dateTo) params.dateTo = effective.dateTo

      const { data } = await api.get('/kanban-lead-conversions', { params })
      setRows(data?.rows || [])
      setCount(data?.count || 0)
    } catch (err) {
      console.error('[KanbanLeadConversions] fetchList error', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async (
    overrides: Partial<{ dateFrom: string; dateTo: string }> = {}
  ) => {
    try {
      setStatsLoading(true)
      const effective = { dateFrom, dateTo, ...overrides }
      const params: Record<string, string> = {}
      if (effective.dateFrom) params.dateFrom = effective.dateFrom
      if (effective.dateTo) params.dateTo = effective.dateTo
      const { data } = await api.get('/kanban-lead-conversions/stats', {
        params
      })
      setStats(data)
    } catch (err) {
      console.error('[KanbanLeadConversions] fetchStats error', err)
    } finally {
      setStatsLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = () => {
    const nextQ = qInput.trim()
    setQ(nextQ)
    setPage(1)
    fetchList({ page: 1, q: nextQ })
  }

  const handleApplyFilters = () => {
    setPage(1)
    fetchList({ page: 1 })
    fetchStats()
  }

  const handleResetFilters = () => {
    setQ('')
    setQInput('')
    setStatus('')
    setKanbanKey('')
    setTicketId('')
    setContactId('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
    fetchList({
      page: 1,
      q: '',
      status: '',
      kanbanKey: '',
      ticketId: '',
      contactId: '',
      dateFrom: '',
      dateTo: ''
    })
    fetchStats({ dateFrom: '', dateTo: '' })
  }

  const handleOpenDetail = async (row: LeadEventRow) => {
    setSelected(row)
    setDetailError(null)
    try {
      setDetailLoading(true)
      const { data } = await api.get(`/kanban-lead-conversions/${row.id}`)
      setSelected(data)
    } catch (err: any) {
      setDetailError(err?.response?.data?.error || err?.message || 'Error cargando detalle')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleRetry = async () => {
    if (!selected) return
    if (selected.responseStatus !== 'failed') return
    try {
      setRetrying(true)
      setDetailError(null)
      const { data } = await api.post(
        `/kanban-lead-conversions/${selected.id}/retry`
      )
      if (data?.record) {
        setSelected(data.record)
        // Refrescar listado y stats
        fetchList()
        fetchStats()
      }
    } catch (err: any) {
      setDetailError(err?.response?.data?.error || err?.message || 'Error al reintentar')
    } finally {
      setRetrying(false)
    }
  }

  // ─── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          {!embedded && (
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FacebookIcon sx={{ color: '#1877F2' }} fontSize="large" />
                <Typography level="h2">Reporte de conversiones Kanban a Meta</Typography>
              </Stack>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Aquí ves si ChatEAM envió la conversión a Meta, en qué pixel quedó
                recibida y con qué conversión personalizada debes buscarla en Facebook.
              </Typography>
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={() => {
                fetchList()
                fetchStats()
              }}
            >
              Actualizar
            </Button>
          </Stack>
        </Stack>

        {/* KPI Cards */}
        <Grid container spacing={2}>
          {[
            {
              key: 'total',
              label: 'Total',
              value: stats?.total ?? 0,
              color: 'neutral' as const,
              icon: <TrendingUpIcon />
            },
            {
              key: 'sentOrSuccess',
              label: 'Recibidas por Meta',
              value: stats?.success ?? 0,
              color: 'success' as const,
              icon: <CheckCircleIcon />
            },
            {
              key: 'failed',
              label: 'Fallidos',
              value: stats?.failed ?? 0,
              color: 'danger' as const,
              icon: <ErrorIcon />
            },
            {
              key: 'pendingSkipped',
              label: 'Pendientes / no enviadas',
              value: (stats?.pending ?? 0) + (stats?.skipped ?? 0),
              color: 'warning' as const,
              icon: <PendingIcon />
            }
          ].map(card => (
            <Grid key={card.key} xs={12} sm={6} md={3}>
              <Card variant="soft" color={card.color}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography level="body-xs">{card.label}</Typography>
                      <Typography level="h3">
                        {statsLoading ? '…' : Number(card.value).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ opacity: 0.6 }}>{card.icon}</Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Buscador */}
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Input
                  placeholder="Buscar por ticket, contacto, etiqueta, conversión Meta, pixel, error o ID de soporte…"
                  value={qInput}
                  onChange={e => setQInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSearch()
                  }}
                  startDecorator={<SearchIcon />}
                  sx={{ flex: 1 }}
                />
                <Button onClick={handleSearch}>Buscar</Button>
              </Stack>

              <Divider />

              <Grid container spacing={1.5}>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs">Estado</Typography>
                  <Select
                    placeholder="Cualquiera"
                    value={status}
                    onChange={(_, v) => setStatus((v as string) || '')}
                    size="sm"
                  >
                    <Option value="">Cualquiera</Option>
                    <Option value="pending">Pendiente</Option>
                    <Option value="sent">Enviada sin confirmar</Option>
                    <Option value="success">Recibida por Meta</Option>
                    <Option value="failed">Fallido</Option>
                    <Option value="skipped">Omitido</Option>
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs">Etiqueta interna</Typography>
                  <Input
                    size="sm"
                    placeholder="ej: interesado"
                    value={kanbanKey}
                    onChange={e => setKanbanKey(e.target.value)}
                  />
                </Grid>
                <Grid xs={6} sm={3} md={2}>
                  <Typography level="body-xs">Ticket ID</Typography>
                  <Input
                    size="sm"
                    type="number"
                    value={ticketId}
                    onChange={e => setTicketId(e.target.value)}
                  />
                </Grid>
                <Grid xs={6} sm={3} md={2}>
                  <Typography level="body-xs">ID contacto</Typography>
                  <Input
                    size="sm"
                    type="number"
                    value={contactId}
                    onChange={e => setContactId(e.target.value)}
                  />
                </Grid>
                <Grid xs={6} sm={3} md={1}>
                  <Typography level="body-xs">Desde</Typography>
                  <Input
                    size="sm"
                    type="date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                  />
                </Grid>
                <Grid xs={6} sm={3} md={1}>
                  <Typography level="body-xs">Hasta</Typography>
                  <Input
                    size="sm"
                    type="date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={handleResetFilters}
                >
                  Limpiar
                </Button>
                <Button
                  variant="solid"
                  startDecorator={<FilterListIcon />}
                  onClick={handleApplyFilters}
                >
                  Aplicar filtros
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Tabla */}
        <Sheet
          variant="outlined"
          sx={{ borderRadius: 'md', overflow: 'auto' }}
        >
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size="md" />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Aún no hay eventos Lead enviados.
              </Typography>
            </Box>
          ) : (
            <Table stickyHeader hoverRow size="sm">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Fecha</th>
                  <th style={{ width: 130 }}>Resultado</th>
                  <th>Conversión en Meta</th>
                  <th style={{ width: 120 }}>Evento</th>
                  <th style={{ width: 100 }}>Ticket</th>
                  <th style={{ width: 150 }}>Contacto</th>
                  <th style={{ width: 150 }}>Pixel / dataset</th>
                  <th style={{ width: 150 }}>Atribución</th>
                  <th style={{ width: 130 }}>Soporte Meta</th>
                  <th style={{ width: 90 }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const attribution = getAttributionInfo(row)
                  const location = getMetaLocation(row)
                  return (
                    <tr key={row.id} style={{ cursor: 'pointer' }}>
                      <td onClick={() => handleOpenDetail(row)}>
                        {formatDate(row.createdAt)}
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={STATUS_COLOR[row.responseStatus]}
                          startDecorator={STATUS_ICON[row.responseStatus]}
                        >
                          {STATUS_LABELS[row.responseStatus] || row.responseStatus}
                        </Chip>
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Stack spacing={0.25}>
                          <Typography level="body-sm">
                            {location.conversionName}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontFamily: 'monospace' }}>
                            ID {maskId(location.conversionId)}
                          </Typography>
                        </Stack>
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Chip size="sm" variant="outlined" color="primary">
                          {row.eventName}
                        </Chip>
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        {row.ticketId ?? '—'}
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Stack spacing={0.25}>
                          <Typography level="body-sm">
                            {row.contact?.name || row.contactId || '—'}
                          </Typography>
                          {row.contact?.number && (
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              {row.contact.number}
                            </Typography>
                          )}
                        </Stack>
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                          {maskId(row.destinationId)}
                        </Typography>
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Tooltip title={attribution.detail}>
                          <Chip size="sm" variant="soft" color="neutral">
                            {attribution.label}
                          </Chip>
                        </Tooltip>
                      </td>
                      <td onClick={() => handleOpenDetail(row)}>
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                          {maskId(row.fbtraceId)}
                        </Typography>
                      </td>
                      <td>
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => handleOpenDetail(row)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Sheet>

        {/* Paginación */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {count.toLocaleString()} eventos — página {page} de {totalPages}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="outlined"
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outlined"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              Siguiente
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* Modal Detalle */}
      <Modal open={!!selected} onClose={() => setSelected(null)}>
        <ModalDialog
          sx={{
            minWidth: { xs: '95vw', md: 760 },
            maxWidth: { xs: '95vw', md: 900 },
            maxHeight: '92vh',
            overflow: 'auto'
          }}
        >
          <ModalClose />
          {selected && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FacebookIcon sx={{ color: '#1877F2' }} />
                <Typography level="h4">Evento Lead #{selected.id}</Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color={STATUS_COLOR[selected.responseStatus]}
                  startDecorator={STATUS_ICON[selected.responseStatus]}
                >
                  {STATUS_LABELS[selected.responseStatus] || selected.responseStatus}
                </Chip>
              </Stack>

              {detailError && (
                <Alert color="danger" variant="soft">
                  {detailError}
                </Alert>
              )}
              {detailLoading && <CircularProgress size="sm" />}

              {(() => {
                const summary = getUserSummary(selected)
                const location = getMetaLocation(selected)
                const attribution = getAttributionInfo(selected)
                const sentBeforeCurrentConversion = wasSentBeforeCurrentConversion(selected)

                return (
                  <Alert color={summary.color} variant="soft">
                    <Stack spacing={1}>
                      <Typography level="title-sm">{summary.title}</Typography>
                      <Typography level="body-sm">{summary.detail}</Typography>
                      <Grid container spacing={1.5}>
                        <Grid xs={12} sm={6}>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            Dónde verlo en Facebook
                          </Typography>
                          <Typography level="body-sm">{location.path}</Typography>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            Conversión personalizada
                          </Typography>
                          <Typography level="body-sm">
                            {location.conversionName} · ID {location.conversionId}
                          </Typography>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            Pixel / dataset
                          </Typography>
                          <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                            {location.pixelId}
                          </Typography>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            Tipo de atribución
                          </Typography>
                          <Typography level="body-sm">
                            {attribution.label}. {attribution.detail}
                          </Typography>
                        </Grid>
                      </Grid>
                      {sentBeforeCurrentConversion && (
                        <Alert color="warning" variant="soft">
                          Este evento se envió antes de la última sincronización de la conversión personalizada actual. Meta puede haberlo recibido en el pixel, pero no necesariamente contarlo en el ID nuevo.
                        </Alert>
                      )}
                    </Stack>
                  </Alert>
                )
              })()}

              {/* Sección Kanban */}
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Etiqueta Kanban
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Nombre etiqueta
                      </Typography>
                      <Typography level="body-sm">
                        {selected.kanbanTagName || selected.kanbanTag?.name || '—'}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Etiqueta interna
                      </Typography>
                      <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                        {selected.kanbanKey || '—'}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID etiqueta
                      </Typography>
                      <Typography level="body-sm">{selected.kanbanTagId ?? '—'}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Origen interno
                      </Typography>
                      <Typography level="body-sm">{selected.source || '—'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Sección Evento */}
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Evento Meta CAPI
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Evento Meta
                      </Typography>
                      <Typography level="body-sm">{selected.eventName}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID deduplicación
                      </Typography>
                      <Typography level="body-sm" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {selected.eventId || '—'}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID ticket
                      </Typography>
                      <Typography level="body-sm">{selected.ticketId ?? '—'}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID contacto
                      </Typography>
                      <Typography level="body-sm">
                        {selected.contactId ?? '—'}
                        {selected.contact?.name ? ` — ${selected.contact.name}` : ''}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID empresa
                      </Typography>
                      <Typography level="body-sm">{selected.companyId}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Enviado a Meta
                      </Typography>
                      <Typography level="body-sm">{formatDate(selected.sentAt)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Sección Destino */}
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Destino Meta (Pixel / Dataset)
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Pixel / dataset ID
                      </Typography>
                      <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                        {selected.destinationId || '—'}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Fuente del pixel/dataset
                      </Typography>
                      <Typography level="body-sm">
                        {selected.destinationSource || '—'}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID soporte Meta
                      </Typography>
                      <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                        {selected.fbtraceId || '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Detalle técnico */}
              <Box component="details" sx={{ '& summary': { cursor: 'pointer' } }}>
                <Typography component="summary" level="title-sm">
                  Detalle técnico para soporte
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-sm" sx={{ mb: 1 }}>
                        Datos enviados a Meta
                      </Typography>
                      <JsonBlock value={selected.customData} />
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-sm" sx={{ mb: 1 }}>
                        Identificadores hasheados
                      </Typography>
                      <JsonBlock value={selected.userData} />
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-sm" sx={{ mb: 1 }}>
                        Respuesta original de Meta
                      </Typography>
                      <JsonBlock value={selected.fbResponse} />
                    </CardContent>
                  </Card>
                </Stack>
              </Box>

              {selected.errorMessage && (
                <Alert color="danger" variant="soft">
                  <Stack spacing={0.5}>
                    <Typography level="title-sm">Error</Typography>
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selected.errorMessage}
                    </Typography>
                  </Stack>
                </Alert>
              )}

              {/* Fechas */}
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={1.5}>
                    <Grid xs={12} sm={4}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Creado
                      </Typography>
                      <Typography level="body-sm">{formatDate(selected.createdAt)}</Typography>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Actualizado
                      </Typography>
                      <Typography level="body-sm">{formatDate(selected.updatedAt)}</Typography>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        Enviado
                      </Typography>
                      <Typography level="body-sm">{formatDate(selected.sentAt)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Acciones */}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {selected.responseStatus === 'failed' && (
                  <Button
                    color="warning"
                    variant="solid"
                    loading={retrying}
                    startDecorator={<ReplayIcon />}
                    onClick={handleRetry}
                  >
                    Reintentar envío
                  </Button>
                )}
                <Button variant="plain" color="neutral" onClick={() => setSelected(null)}>
                  Cerrar
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  )
}
